import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useRouteMatch, useHistory } from "react-router-dom";
import IntelLayout from "@components/layout/layout";
import { BRAND } from "@core/brand/brand-config";
import { globalCountryDetailsDictionary } from "@core/global/global-country-details";
import { loadConversations, loadConversation } from "@redux/Hermes/hermes.action";
import { conversationCleared } from "@redux/Hermes/hermesSlice";
import { getLastSentConversationId, forgetLastSentConversation } from "./utils/lastConversation";
import useHermesStream from "./hooks/useHermesStream";
import useHermesPolling from "./hooks/useHermesPolling";
import useHermesAnalytics from "./hooks/useHermesAnalytics";
import ChatShell from "./components/ChatShell";
import type { AppDispatch, HermesRootState } from "./types";

export default function Hermes() {
  const dispatch = useDispatch<AppDispatch>();
  const history = useHistory();
  const match = useRouteMatch<{ conversationId?: string }>("/hermes-chat/:conversationId");
  const conversationId = match?.params?.conversationId;

  const activeConversationId = useSelector((state: HermesRootState) => state.hermes.activeConversationId);
  const status = useSelector((state: HermesRootState) => state.hermes.status);
  const creditData = useSelector((state: HermesRootState) => state.userDetails?.creditDetails?.data);
  const isGlobalPlan = creditData?.find((plan) => plan?.is_active && plan?.user_active_plan?.toLowerCase()?.includes("global"))?.navigate === "search";

  const { sendMessage, abort } = useHermesStream();
  const analytics = useHermesAnalytics();
  // Polls GET /messages whenever a response is being generated in the background
  // for the conversation in view (status === "awaiting_response").
  useHermesPolling();

  // Hermes is the landing page, so the SSO entry and the "/" redirect drop the
  // user here with no market context — unlike the sidebar, where country/type
  // ride in from the page they came from. The company links in an answer forward
  // those params to the profile page, which otherwise falls back to US/import.
  // Default to the all-country dataset so the profile opens on every market the
  // user has, the same fallback the notification links use.
  useEffect(() => {
    const params = new URLSearchParams(history.location.search);
    const country = params.get("country");
    const type = params.get("type");

    const allCountryParam = new URLSearchParams(globalCountryDetailsDictionary.multi_country.countryUrlDashboard).get("country") ?? "";
    const holdsStaleAllCountryDefault = isGlobalPlan && country === allCountryParam;
    if (country && type && !holdsStaleAllCountryDefault) return;

    const defaultMarket = isGlobalPlan ? globalCountryDetailsDictionary.global_plan : globalCountryDetailsDictionary.multi_country;
    params.set("country", new URLSearchParams(defaultMarket.countryUrlDashboard).get("country") ?? "");
    params.set("type", defaultMarket.countryType);
    history.replace(`${history.location.pathname}?${params.toString()}`);
  }, [history, isGlobalPlan]);

  useEffect(() => {
    dispatch(loadConversations());
    return () => {
      abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (conversationId) {
      dispatch(loadConversation(conversationId));
      return;
    }

    // No conversation in the URL — but if the user sent a message before
    // navigating away (reload, another page, another conversation), the URL
    // may never have caught up (it only updates once the turn finishes).
    // Send them back to it instead of a blank new conversation; the existing
    // fulfilled-load logic figures out whether that turn is still streaming
    // or already landed.
    const lastSentConversationId = getLastSentConversationId();
    if (lastSentConversationId) {
      history.replace(`/hermes-chat/${lastSentConversationId}${history.location.search}`);
      return;
    }

    abort();
    dispatch(conversationCleared());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId]);

  useEffect(() => {
    if (activeConversationId && !conversationId && status === "idle") {
      history.replace(`/hermes-chat/${activeConversationId}${history.location.search}`);
    }
  }, [activeConversationId, conversationId, history, status]);

  // The retention rule: keep reopening the last-sent conversation on return
  // while its turn is in progress, and stop once it has both finished AND been
  // viewed. This effect handles the "viewed" half — the user is looking at the
  // last-sent conversation (activeConversationId === bookmark) and it has fully
  // landed (status "idle") — so we drop the bookmark and the next return starts
  // a new chat. While the turn is still generating (status "awaiting_response"/
  // "streaming") the bookmark stays, so a reload/navigation keeps resuming it.
  useEffect(() => {
    if (status === "idle" && activeConversationId && activeConversationId === getLastSentConversationId()) {
      forgetLastSentConversation();
    }
  }, [status, activeConversationId]);

  const handleNewChat = () => {
    analytics.newChatStarted();
    forgetLastSentConversation();
    abort();
    dispatch(conversationCleared());
    history.push(`/hermes-chat${history.location.search}`);
  };

  const handleSelectConversation = (id: string) => {
    if (id === conversationId) return;
    analytics.conversationOpened({ conversationId: id });
    history.push(`/hermes-chat/${id}${history.location.search}`);
  };

  return (
    <IntelLayout type="hermes" pageTitle={`${BRAND.aiAssistantName} | ${BRAND.titleSuffix}`}>
      <ChatShell sendMessage={sendMessage} abort={abort} onNewChat={handleNewChat} onSelectConversation={handleSelectConversation} />
    </IntelLayout>
  );
}
